<template>
  <DialogLayout
    :value="value"
    width="900px"
    :title="isEditing ? 'Edit Task Rule' : 'Add New Task Rule'"
    @input="$emit('input', $event)"
  >
    <AlertsAnimated :alerts="localAlerts" />
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submitTaskRule)">
        <v-row dense>
          <v-col v-if="!isEditing" cols="12">
            <ValidationProvider
              v-slot="{ errors }"
              name="Organization"
              rules="required"
            >
              <OrganizationAutocomplete
                v-model="selectedOrganization"
                :organization="defaultOrganization"
                :error-messages="errors"
                label="Select Region / Facility"
                :prefetch="true"
                max-width="100%"
                class="mb-2"
                :filter="organizationsFilter"
                clearable
                @input="handleOrganizationSelection"
              />
              <div
                v-if="errors.length > 0"
                class="v-messages theme--light error--text"
                role="alert"
              >
                <div class="v-messages__wrapper ml-8">
                  <div
                    v-for="error in errors"
                    :key="error"
                    class="v-messages__message"
                  >
                    {{ "Region / Facility is required" }}
                  </div>
                </div>
              </div>
            </ValidationProvider>
          </v-col>

          <v-col cols="6">
            <ValidationProvider
              v-slot="{ errors }"
              name="Task Name"
              rules="required|min:2|max:250"
            >
              <v-text-field
                v-model="taskRuleForm.name"
                label="Task Name"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col v-if="!isManageOverlocks" cols="6">
            <ValidationProvider
              v-slot="{ errors }"
              name="Task Type"
              rules="required"
            >
              <v-select
                v-model="taskRuleForm.taskTypeId"
                label="Task Type"
                :items="formattedTaskTypes"
                item-text="name"
                item-value="id"
                :error-messages="errors"
                @change="handleTaskTypeChange"
              />
            </ValidationProvider>
          </v-col>

          <v-col v-if="!isManageOverlocks" cols="6">
            <ValidationProvider
              v-slot="{ errors }"
              name="Recurrence Frequency"
              rules="required"
            >
              <v-select
                v-model="taskRuleForm.frequency.type"
                label="Recurrence Frequency"
                :items="
                  isPreInstallDevices
                    ? preInstallScheduleTypeOptions
                    : scheduleTypeOptions
                "
                :error-messages="errors"
                @change="handleScheduleTypeChange"
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="6">
            <ValidationProvider
              v-slot="{ errors }"
              name="Priority"
              rules="required"
            >
              <v-select
                v-model="taskRuleForm.priority"
                label="Priority"
                :items="priorityOptions"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <template
            v-if="
              !isManageOverlocks &&
              taskRuleForm.frequency &&
              taskRuleForm.frequency.type !== 'daily' &&
              taskRuleForm.frequency.type !== 'one-time'
            "
          >
            <v-col cols="4">
              <ValidationProvider
                v-slot="{ errors }"
                name="Interval"
                rules="required|numeric|min_value:1"
              >
                <v-text-field
                  v-model.number="taskRuleForm.frequency.interval"
                  label="Every"
                  type="number"
                  min="1"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-col>
          </template>

          <template
            v-if="
              !isManageOverlocks &&
              taskRuleForm.frequency &&
              taskRuleForm.frequency.type === 'weekly'
            "
          >
            <v-col cols="8">
              <ValidationProvider
                v-slot="{ errors }"
                name="Days of Week"
                rules="required"
              >
                <v-select
                  v-model="taskRuleForm.frequency.daysOfWeek"
                  label="Days of Week"
                  :items="dayOfWeekOptions"
                  multiple
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-col>
          </template>

          <template
            v-if="
              !isManageOverlocks &&
              taskRuleForm.frequency &&
              taskRuleForm.frequency.type === 'monthly'
            "
          >
            <v-col cols="4">
              <ValidationProvider
                v-slot="{ errors }"
                name="Day of Month"
                rules="required|numeric|min_value:1|max_value:31"
              >
                <v-text-field
                  v-model.number="taskRuleForm.frequency.dayOfMonth"
                  label="Day of Month"
                  type="number"
                  min="1"
                  max="31"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-col>
          </template>

          <template
            v-if="
              !isManageOverlocks &&
              taskRuleForm.frequency &&
              taskRuleForm.frequency.type === 'yearly'
            "
          >
            <v-col cols="4">
              <ValidationProvider
                v-slot="{ errors }"
                name="Month of Year"
                rules="required"
              >
                <v-select
                  v-model="taskRuleForm.frequency.monthOfYear"
                  label="Month of Year"
                  :items="monthOfYearOptions"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-col>
          </template>

          <template
            v-if="
              !isManageOverlocks &&
              taskRuleForm.frequency &&
              taskRuleForm.frequency.type === 'custom'
            "
          >
            <v-col cols="4">
              <ValidationProvider
                v-slot="{ errors }"
                name="Custom Unit"
                rules="required"
              >
                <v-select
                  v-model="customUnit"
                  label="Unit"
                  :items="customUnitOptions"
                  :error-messages="errors"
                  @change="handleCustomUnitChange"
                />
              </ValidationProvider>
            </v-col>

            <template v-if="customUnit === 'weeks'">
              <v-col cols="8">
                <v-select
                  v-model="taskRuleForm.frequency.daysOfWeek"
                  label="Days of Week"
                  :items="dayOfWeekOptions"
                  multiple
                />
              </v-col>
            </template>

            <template v-if="customUnit === 'months'">
              <v-col cols="4">
                <v-text-field
                  v-model.number="taskRuleForm.frequency.dayOfMonth"
                  label="Day of Month"
                  type="number"
                  min="1"
                  max="31"
                />
              </v-col>
            </template>

            <template v-if="customUnit === 'years'">
              <v-col cols="4">
                <v-select
                  v-model="taskRuleForm.frequency.monthOfYear"
                  label="Month of Year"
                  :items="monthOfYearOptions"
                />
              </v-col>
            </template>
          </template>

          <v-col cols="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedStartDate"
                  label="Start Date"
                  readonly
                  prepend-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="taskRuleForm.startsAt"
                no-title
                :min="new Date().toISOString().substr(0, 10)"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedEndDate"
                  label="End Date"
                  readonly
                  prepend-icon="mdi-calendar"
                  clearable
                  :disabled="
                    taskRuleForm.frequency &&
                    taskRuleForm.frequency.type === 'one-time'
                  "
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="taskRuleForm.endsAt = null"
                />
              </template>
              <v-date-picker
                v-model="taskRuleForm.endsAt"
                no-title
                :min="
                  taskRuleForm.startsAt ||
                  new Date().toISOString().substr(0, 10)
                "
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="6">
            <v-btn
              outlined
              color="primary"
              small
              @click="showNotificationModal = true"
            >
              View Notification Preferences
            </v-btn>
          </v-col>

          <v-col
            v-if="!isManageOverlocks && !isPreInstallDevices"
            cols="6"
            class="d-flex align-center"
          >
            <v-checkbox
              class="mt-0"
              small
              v-model="taskRuleForm.actions.photoVideoRequired"
              label="Photo/Video Required"
            />
          </v-col>

          <v-col class="mt-4" v-if="isWalkaroundChecklist" cols="12">
            <v-card outlined>
              <v-card-subtitle class="pt-4 pb-0 px-4">
                <div class="d-flex justify-space-between align-center mb-1">
                  Create/Edit Checklist
                  <div>
                    <v-btn
                      text
                      color="success"
                      outlined
                      small
                      class="mr-2"
                      :loading="uploading"
                      @click="$refs.additionalFileInput.click()"
                    >
                      Add From File &nbsp;
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            color="info"
                            small
                            class="mr-2"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>The first row will be removed as header row</span>
                      </v-tooltip>
                    </v-btn>

                    <v-btn
                      text
                      color="error"
                      outlined
                      small
                      @click="clearChecklist"
                    >
                      Clear All
                    </v-btn>
                  </div>
                </div>
              </v-card-subtitle>

              <v-container class="pt-4 pb-4" fluid>
                <!-- Additional file input for adding more items -->
                <input
                  ref="additionalFileInput"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style="display: none"
                  @change="handleAdditionalFileUpload"
                />

                <v-row
                  v-for="(item, index) in checklistItems"
                  :key="index"
                  no-gutters
                  class="mb-2"
                >
                  <v-col cols="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="item.required"
                      :label="item.required ? 'Required' : 'Optional'"
                      :true-value="true"
                      :false-value="false"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="10">
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model="item.description"
                        :label="`Checklist item ${index + 1}`"
                        dense
                        hide-details
                        class="flex-grow-1"
                      />
                      <v-btn
                        icon
                        color="error"
                        class="mt-4"
                        @click="removeChecklistItem(index)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn text color="primary" small @click="addChecklistItem">
                      <v-icon left small>mdi-plus</v-icon>
                      Add item
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>

          <v-col v-if="isPreInstallDevices" cols="12" class="">
            <v-card>
              <v-card-title class="pb-1">
                Pre-Installed Devices Configuration
              </v-card-title>

              <v-card-text class="pb-0">
                <v-row dense>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Pre-Install Logic Method"
                      rules="required"
                    >
                      <div class="mb-1 font-weight-medium">Logic Selection</div>
                      <v-radio-group
                        v-model="taskRuleForm.preInstallMethod"
                        :error-messages="errors"
                        @change="handlePreInstallMethodChange"
                      >
                        <div class="method-container">
                          <v-radio
                            label="Method A: Vacancy Threshold"
                            value="vacancy_threshold"
                            class="text-subtitle-2 text--secondary"
                          />
                          <div class="method-inputs ml-8 mt-2">
                            <v-row dense>
                              <v-col cols="12" class="mb-2">
                                <span class="text-caption text--secondary">
                                  If &lt;
                                  <v-text-field
                                    v-model.number="
                                      taskRuleForm.vacancyThreshold
                                    "
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="inline-number-input"
                                    dense
                                    hide-details
                                  />
                                  Vacancies, Target =
                                  <v-text-field
                                    v-model.number="
                                      taskRuleForm.targetBelowThreshold
                                    "
                                    type="number"
                                    min="0"
                                    class="inline-number-input"
                                    dense
                                    hide-details
                                  />
                                  Units. If ≥
                                  <v-text-field
                                    v-model.number="
                                      taskRuleForm.vacancyThreshold
                                    "
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="inline-number-input"
                                    dense
                                    hide-details
                                    readonly
                                  />
                                  Vacancies, Target =
                                  <v-text-field
                                    v-model.number="
                                      taskRuleForm.targetAtOrAboveThreshold
                                    "
                                    type="number"
                                    min="0"
                                    class="inline-number-input"
                                    dense
                                    hide-details
                                  />
                                  Units.
                                </span>
                              </v-col>
                            </v-row>
                          </div>
                        </div>

                        <div class="method-container">
                          <v-radio
                            label="Method B: High Penetration"
                            value="high_penetration"
                            class="text-subtitle-2 text--secondary"
                          />
                          <div class="method-inputs ml-8 mt-2">
                            <v-row dense>
                              <v-col cols="12" class="mb-2">
                                <span class="text-caption text--secondary">
                                  Fixed Target =
                                  <v-text-field
                                    v-model.number="
                                      taskRuleForm.fixedTargetUnits
                                    "
                                    type="number"
                                    min="1"
                                    class="inline-number-input"
                                    dense
                                    hide-details
                                  />
                                  Units. Prioritizes high-demand sizes.
                                </span>
                              </v-col>
                            </v-row>
                          </div>
                        </div>

                        <div class="method-container">
                          <v-radio
                            label="Method C: Unit Type Coverage"
                            value="unit_type_coverage"
                            class="text-subtitle-2 text--secondary"
                          />
                          <div class="method-inputs ml-8 mt-2">
                            <v-row dense>
                              <v-col cols="12" class="mb-2">
                                <span class="text-caption text--secondary">
                                  Target =
                                  <v-text-field
                                    v-model.number="taskRuleForm.devicesPerUnit"
                                    type="number"
                                    min="1"
                                    style="
                                      display: inline-block;
                                      width: 80px;
                                      margin: 0 8px;
                                    "
                                    dense
                                    hide-details
                                  />
                                  Device per Unique Unit Size/Type.
                                </span>
                              </v-col>
                            </v-row>
                          </div>
                        </div>
                      </v-radio-group>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <ValidationProvider
              v-slot="{ errors }"
              name="Task Description"
              rules="required"
            >
              <v-textarea
                v-model="taskRuleForm.description"
                label="Task Description"
                rows="3"
                :error-messages="errors"
                :maxlength="500"
                counter="500"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn @click="handleClose" class="mr-2">Cancel</v-btn>
            <v-btn
              color="success"
              type="submit"
              :loading="loading"
              :disabled="!isFormValid"
            >
              {{ isEditing ? "Update Task Rule" : "Add New Task Rule" }}
            </v-btn>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>

    <DialogLayout
      v-model="showNotificationModal"
      title="Notification Settings"
      width="1400px"
    >
      <NotificationModal
        v-model="taskRuleForm.notificationPreference"
        notification-type="task_rule"
        :read-only="false"
        @save="handleNotificationSave"
        @validation-error="handleNotificationValidationError"
      />
    </DialogLayout>
  </DialogLayout>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import {
  required,
  min,
  max,
  numeric,
  min_value,
  max_value,
} from "vee-validate/dist/rules";
import DialogLayout from "@layout/DialogLayout.vue";
import NotificationModal from "./NotificationModal.vue";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import useUser from "@components/authentication/useUser";
import { titleCase } from "@tod-ui/helpers/strings";
import { formatDate } from "@tod-ui/helpers/datetime";
import {
  EXTRACT_TASK_CHECKLIST,
  TASK_TYPES,
  CREATE_TASK_RULE,
  UPDATE_TASK_RULE,
} from "./graphql";
import OrganizationAutocomplete from "@components/utils/OrganizationAutocomplete.vue";

extend("required", required);
extend("min", min);
extend("max", max);
extend("numeric", numeric);
extend("min_value", min_value);
extend("max_value", max_value);

export default {
  name: "DialogTaskRule",

  components: {
    DialogLayout,
    NotificationModal,
    OrganizationAutocomplete,
    AlertsAnimated,
    ValidationObserver,
    ValidationProvider,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    taskRule: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    defaultOrganizationId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { defaultOrganization } = useUser();
    const { addLocalAlert, clearAllAlerts, localAlerts } = useAlerts();

    return {
      defaultOrganization,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
    };
  },
  data() {
    return {
      loading: false,
      uploading: false,
      startDateMenu: false,
      endDateMenu: false,
      showNotificationModal: false,
      showInlineChecklist: false,
      checklistItems: [{ description: "", required: true }],
      parentFacilityId: "",
      customUnit: "days",
      taskTypes: [],
      selectedOrganization: null,

      taskRuleForm: {
        organizationId: "",
        name: "",
        taskTypeId: "",
        description: "",
        preInstallMethod: null,
        vacancyThreshold: null,
        targetBelowThreshold: null,
        targetAtOrAboveThreshold: null,
        fixedTargetUnits: null,
        devicesPerUnit: null,
        frequency: {
          type: "one-time",
          interval: 1,
          daysOfWeek: [],
          dayOfMonth: null,
          monthOfYear: null,
        },
        priority: "moderate",
        startsAt: this.getCurrentDate(),
        endsAt: "2099-12-31",
        status: "active",
        actions: {
          photoVideoRequired: false,
        },
        notificationPreference: this.getDefaultNotificationPreference(),
      },

      // NOTE: preInstallScheduleTypeOptions is a computed (see below) — it
      // narrows to Daily for Method C and Weekly for Methods A/B. Per PRD.

      scheduleTypeOptions: [
        { text: "One Time", value: "one-time" },
        { text: "Daily", value: "daily" },
        { text: "Weekly", value: "weekly" },
        { text: "Monthly", value: "monthly" },
        { text: "Yearly", value: "yearly" },
        { text: "Custom", value: "custom" },
      ],

      priorityOptions: [
        { text: "Critical", value: "critical" },
        { text: "Severe", value: "severe" },
        { text: "Substantial", value: "substantial" },
        { text: "Moderate", value: "moderate" },
        { text: "Low", value: "low" },
      ],

      customUnitOptions: [
        { text: "Days", value: "days" },
        { text: "Weeks", value: "weeks" },
        { text: "Months", value: "months" },
        { text: "Years", value: "years" },
      ],

      dayOfWeekOptions: [
        { text: "Monday", value: "monday" },
        { text: "Tuesday", value: "tuesday" },
        { text: "Wednesday", value: "wednesday" },
        { text: "Thursday", value: "thursday" },
        { text: "Friday", value: "friday" },
        { text: "Saturday", value: "saturday" },
        { text: "Sunday", value: "sunday" },
      ],

      monthOfYearOptions: [
        { text: "January", value: 1 },
        { text: "February", value: 2 },
        { text: "March", value: 3 },
        { text: "April", value: 4 },
        { text: "May", value: 5 },
        { text: "June", value: 6 },
        { text: "July", value: 7 },
        { text: "August", value: 8 },
        { text: "September", value: 9 },
        { text: "October", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ],
    };
  },

  computed: {
    organizationsFilter() {
      return {
        types: ["region", "facility"],
      };
    },
    formattedTaskTypes() {
      return this.taskTypes
        .filter(
          (type) =>
            type.status === "active" &&
            [
              "maintenance",
              "custom",
              "walkaround_checklist",
              "pre_install_devices",
            ].includes(type.name?.toLowerCase())
        )
        .map((type) => ({
          ...type,
          name: this.titleCase(type.name),
        }));
    },

    isWalkaroundChecklist() {
      const selectedTaskType = this.taskTypes.find(
        (type) => type.id === this.taskRuleForm.taskTypeId
      );
      return (
        selectedTaskType?.name?.toLowerCase().includes("walkaround") ||
        selectedTaskType?.name?.toLowerCase().includes("checklist")
      );
    },

    isPreInstallDevices() {
      const selectedTaskType = this.taskTypes.find(
        (type) => type.id === this.taskRuleForm.taskTypeId
      );
      return selectedTaskType?.name?.toLowerCase() === "pre_install_devices";
    },

    // Frequency restrictions per PRD:
    //   Method A / B  →  Weekly only
    //   Method C       →  Daily or Weekly
    preInstallScheduleTypeOptions() {
      if (this.taskRuleForm.preInstallMethod === "unit_type_coverage") {
        return [
          { text: "Daily", value: "daily" },
          { text: "Weekly", value: "weekly" },
        ];
      }
      return [{ text: "Weekly", value: "weekly" }];
    },

    isPreInstallMethodValid() {
      const method = this.taskRuleForm.preInstallMethod;
      if (!method) return false;
      if (method === "vacancy_threshold") {
        return (
          this.taskRuleForm.vacancyThreshold != null &&
          this.taskRuleForm.vacancyThreshold !== "" &&
          this.taskRuleForm.targetBelowThreshold != null &&
          this.taskRuleForm.targetBelowThreshold !== "" &&
          this.taskRuleForm.targetAtOrAboveThreshold != null &&
          this.taskRuleForm.targetAtOrAboveThreshold !== ""
        );
      }
      if (method === "high_penetration") {
        return (
          this.taskRuleForm.fixedTargetUnits != null &&
          this.taskRuleForm.fixedTargetUnits !== "" &&
          this.taskRuleForm.fixedTargetUnits > 0
        );
      }
      if (method === "unit_type_coverage") {
        return (
          this.taskRuleForm.devicesPerUnit != null &&
          this.taskRuleForm.devicesPerUnit !== "" &&
          this.taskRuleForm.devicesPerUnit > 0
        );
      }
      return false;
    },

    isManageOverlocks() {
      const selectedTaskType = this.taskTypes.find(
        (type) => type.id === this.taskRuleForm.taskTypeId
      );
      return selectedTaskType?.name?.toLowerCase() === "manage_overlocks";
    },

    isFormValid() {
      const basicValidation =
        this.taskRuleForm.name &&
        this.taskRuleForm.taskTypeId &&
        this.taskRuleForm.description &&
        this.taskRuleForm.priority &&
        this.taskRuleForm.organizationId &&
        this.hasValidNotificationPreference;

      if (this.isManageOverlocks) {
        return basicValidation;
      }

      if (this.isPreInstallDevices) {
        const methodValid = this.isPreInstallMethodValid;
        return (
          basicValidation &&
          this.taskRuleForm.frequency &&
          this.taskRuleForm.frequency.type &&
          this.taskRuleForm.preInstallMethod &&
          methodValid
        );
      }

      return (
        basicValidation &&
        this.taskRuleForm.frequency &&
        this.taskRuleForm.frequency.type
      );
    },

    hasValidNotificationPreference() {
      const prefs = this.taskRuleForm.notificationPreference;
      if (!prefs) return false;

      const checkNotificationGroup = (group) => {
        if (!group) return false;
        return Object.values(group).some((value) => {
          if (typeof value === "boolean") return value;
          if (typeof value === "object" && value !== null) {
            return Object.values(value).some((v) => Boolean(v));
          }
          return Boolean(value);
        });
      };

      return (
        checkNotificationGroup(prefs.notifyAssignment) ||
        checkNotificationGroup(prefs.notifyTodo) ||
        checkNotificationGroup(prefs.notifyOverdue)
      );
    },

    notificationValidationMessage() {
      if (!this.hasValidNotificationPreference) {
        return "At least one notification preference must be enabled";
      }
      return "";
    },

    formattedStartDate() {
      return formatDate(this.taskRuleForm.startsAt);
    },

    formattedEndDate() {
      return formatDate(this.taskRuleForm.endsAt);
    },
  },
  apollo: {
    taskTypes: {
      query: TASK_TYPES,
      variables() {
        return {
          status: "active",
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.taskTypes || [];
      },
      error(error) {
        console.error("Error fetching task types:", error);
      },
    },
  },

  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          this.clearAllAlerts();
          this.$nextTick(() => {
            if (this.$refs.observer) {
              this.$refs.observer.reset();
            }
          });

          if (this.taskRule && this.isEditing) {
            this.$nextTick(() => {
              this.populateForm(this.taskRule);
            });
          } else if (!this.isEditing) {
            this.resetForm();
          }
        } else {
          this.clearAllAlerts();
          this.$nextTick(() => {
            if (this.$refs.observer) {
              this.$refs.observer.reset();
            }
          });
        }
      },
      immediate: true,
    },

    taskRule: {
      handler(newTaskRule, oldTaskRule) {
        if (newTaskRule && this.isEditing && this.value) {
          if (!oldTaskRule || oldTaskRule.id !== newTaskRule.id) {
            this.$nextTick(() => {
              this.populateForm(newTaskRule);
            });
          }
        }
      },
      deep: true,
      immediate: true,
    },

    showNotificationModal: {
      handler(isOpen) {
        if (isOpen) {
          // Force a fresh notification preference when modal opens
          this.$nextTick(() => {
            // Trigger a re-render of notification modal with current data
            const currentPrefs = {
              ...this.taskRuleForm.notificationPreference,
            };
            this.taskRuleForm.notificationPreference = {};
            this.$nextTick(() => {
              this.taskRuleForm.notificationPreference = currentPrefs;
            });
          });
        }
      },
    },
  },

  methods: {
    getCurrentDate() {
      return new Date().toISOString().substr(0, 10);
    },
    cleanGraphQLObject(obj) {
      if (obj === null || obj === undefined) {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map((item) => this.cleanGraphQLObject(item));
      }

      if (typeof obj === "object") {
        const cleaned = {};

        Object.keys(obj).forEach((key) => {
          // Skip GraphQL metadata fields
          if (!key.startsWith("__")) {
            const value = obj[key];
            if (value !== null && value !== undefined) {
              cleaned[key] = this.cleanGraphQLObject(value);
            }
          }
        });

        return cleaned;
      }

      return obj;
    },

    handleOrganizationSelection(organization) {
      if (organization && organization.id) {
        this.taskRuleForm.organizationId = organization.id;
        this.selectedOrganization = organization;
      }
    },

    handleParentFacilityChange(facilityId) {
      this.parentFacilityId = facilityId;
    },

    handleTaskTypeChange() {
      if (this.isWalkaroundChecklist) {
        this.checklistItems = [{ description: "", required: true }];
        this.showInlineChecklist = false;
      }

      if (this.isPreInstallDevices) {
        // Pre-install defaults to Weekly on first switch — but
        // handlePreInstallMethodChange will swap to Daily if Method C is
        // chosen. Methods A/B stay Weekly per PRD.
        this.handleScheduleTypeChange("weekly");
      } else {
        this.taskRuleForm.preInstallMethod = null;
      }
    },

    handlePreInstallMethodChange(method) {
      const methodNames = {
        vacancy_threshold: {
          description: "Select Units and Assign devices as required",
        },
        high_penetration: {
          description: "Select Units and Assign devices as required",
        },
        unit_type_coverage: {
          description: "Select Unit type and Assign devices a required",
        },
      };

      const preset = methodNames[method];
      if (preset) {
        this.taskRuleForm.description = preset.description;
      }

      // Clear fields that belong to the OTHER method(s)
      if (method === "high_penetration") {
        this.taskRuleForm.vacancyThreshold = null;
        this.taskRuleForm.targetBelowThreshold = null;
        this.taskRuleForm.targetAtOrAboveThreshold = null;
        this.taskRuleForm.devicesPerUnit = null;
      } else if (method === "vacancy_threshold") {
        this.taskRuleForm.fixedTargetUnits = null;
        this.taskRuleForm.devicesPerUnit = null;
      } else if (method === "unit_type_coverage") {
        this.taskRuleForm.vacancyThreshold = null;
        this.taskRuleForm.targetBelowThreshold = null;
        this.taskRuleForm.targetAtOrAboveThreshold = null;
        this.taskRuleForm.fixedTargetUnits = null;
      }

      // Per PRD frequency rules:
      //   Method A / B  →  Weekly only
      //   Method C       →  Daily or Weekly (default Daily on first switch)
      // Only force a change when the current pick is invalid for the new
      // method (don't clobber a valid Daily/Weekly choice on Method C).
      const allowed =
        method === "unit_type_coverage" ? ["daily", "weekly"] : ["weekly"];
      if (!allowed.includes(this.taskRuleForm.frequency.type)) {
        const fallback = method === "unit_type_coverage" ? "daily" : "weekly";
        this.handleScheduleTypeChange(fallback);
      }
    },

    handleScheduleTypeChange(scheduleType = null) {
      const type = scheduleType || this.taskRuleForm.frequency.type;

      this.taskRuleForm.frequency = {
        type: type,
        interval:
          type === "daily" || type === "one-time"
            ? 1
            : this.taskRuleForm.frequency.interval || 1,
        daysOfWeek: [],
        dayOfMonth: null,
        monthOfYear: null,
      };

      switch (type) {
        case "one-time":
          this.taskRuleForm.frequency.interval = 1;
          break;
        case "daily":
          this.taskRuleForm.frequency.interval = 1;
          break;
        case "weekly":
          this.taskRuleForm.frequency.daysOfWeek = ["monday"];
          break;
        case "monthly":
          this.taskRuleForm.frequency.dayOfMonth = 1;
          break;
        case "yearly":
          this.taskRuleForm.frequency.monthOfYear = 1;
          break;
        case "custom":
          this.customUnit = "days";
          break;
      }
    },

    handleCustomUnitChange() {
      this.taskRuleForm.frequency.daysOfWeek = [];
      this.taskRuleForm.frequency.dayOfMonth = null;
      this.taskRuleForm.frequency.monthOfYear = null;

      switch (this.customUnit) {
        case "weeks":
          this.taskRuleForm.frequency.daysOfWeek = ["monday"];
          break;
        case "months":
          this.taskRuleForm.frequency.dayOfMonth = 1;
          break;
        case "years":
          this.taskRuleForm.frequency.monthOfYear = 1;
          break;
      }
    },

    addChecklistItem() {
      this.checklistItems.push({ description: "", required: true });
    },

    removeChecklistItem(index) {
      this.checklistItems.splice(index, 1);
      if (this.checklistItems.length === 0) {
        this.checklistItems = [{ description: "", required: true }];
      }
    },

    clearChecklist() {
      this.checklistItems = [{ description: "", required: true }];
      this.showInlineChecklist = false;
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        await this.extractAndAddToChecklist(file);

        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }

        this.showInlineChecklist = true;
      }
    },

    async handleAdditionalFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        await this.extractAndAddToChecklist(file);

        if (this.$refs.additionalFileInput) {
          this.$refs.additionalFileInput.value = "";
        }
      }
    },

    async extractAndAddToChecklist(file) {
      this.uploading = true;

      try {
        const result = await this.$apollo.mutate({
          mutation: EXTRACT_TASK_CHECKLIST,
          variables: {
            file: file,
            hasHeader: true,
          },
        });

        const extractedItems = result.data.extractTaskChecklist || [];

        if (extractedItems.length === 0) {
          this.addLocalAlert({
            type: "warning",
            message: "No checklist items found in the file",
            timeout: 5,
          });
          return;
        }

        if (
          this.checklistItems.length === 1 &&
          !this.checklistItems[0].description.trim()
        ) {
          this.checklistItems = [];
        }

        extractedItems.forEach((description) => {
          if (description && description.trim()) {
            this.checklistItems.push({
              description: description.trim(),
              required: true,
            });
          }
        });

        this.addLocalAlert({
          type: "success",
          message: `Added ${extractedItems.length} items from file to checklist`,
          timeout: 5,
        });
      } catch (error) {
        console.error("Error extracting checklist items:", error);
        this.addLocalAlert({
          type: "error",
          message: "Error extracting items from file. Please try again.",
          timeout: 5,
        });
      } finally {
        this.uploading = false;
      }
    },

    acceptExtractedChecklist() {
      this.checklistItems = this.extractedChecklist.map((description) => ({
        description: description || "",
        required: true,
      }));

      this.showInlineChecklist = true;
      this.extractedChecklist = [];

      this.addLocalAlert({
        type: "success",
        message: "Checklist items accepted and ready for editing",
        timeout: 5,
      });
    },

    clearExtractedChecklist() {
      this.extractedChecklist = [];
      this.uploadedFileName = "";
      this.uploadedFile = null;

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    clearUploadedFile() {
      this.uploadedFileName = "";
      this.uploadedFile = null;
      this.extractedChecklist = [];

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      if (this.$refs.additionalFileInput) {
        this.$refs.additionalFileInput.value = "";
      }
    },

    saveNotificationSettings() {
      this.showNotificationModal = false;
      this.addLocalAlert({
        type: "success",
        message: "Notification settings saved",
        timeout: 5,
      });
    },
    populateForm(taskRule) {
      const formatDateForPicker = (dateString) => {
        if (!dateString) return null;
        try {
          const date = new Date(dateString);
          return date.toISOString().substr(0, 10);
        } catch (error) {
          console.error("Error formatting date:", dateString, error);
          return null;
        }
      };

      let frequency = taskRule.frequency;
      const isManageOverlocksTask =
        taskRule.taskType?.name?.toLowerCase() === "manage_overlocks";

      if (isManageOverlocksTask) {
        frequency = { type: "daily", interval: 1 };
      } else if (frequency === null || frequency === undefined) {
        frequency = { type: "daily", interval: 1 };
      } else if (typeof frequency === "string") {
        try {
          frequency = JSON.parse(frequency);
        } catch (error) {
          console.error("Error parsing frequency:", frequency, error);
          frequency = { type: "daily", interval: 1 };
        }
      }

      let notificationPreference = taskRule.notificationPreference;
      if (typeof notificationPreference === "string") {
        try {
          notificationPreference = JSON.parse(notificationPreference);
        } catch (error) {
          console.error(
            "Error parsing notificationPreference:",
            notificationPreference,
            error
          );
          notificationPreference = this.getDefaultNotificationPreference();
        }
      }

      const cleanNotificationPreference = this.cleanGraphQLObject(
        notificationPreference || this.getDefaultNotificationPreference()
      );

      const actions = taskRule.actions || {};

      const c =
        taskRule.conditions &&
        taskRule.conditions.event === "pre_install_devices"
          ? taskRule.conditions
          : null;

      this.taskRuleForm = {
        id: taskRule.id,
        organizationId:
          taskRule.organizationId || taskRule.organization?.id || "",
        name: taskRule.name || "",
        taskTypeId: taskRule.taskTypeId || taskRule.taskType?.id || "",
        description: taskRule.description || "",
        preInstallMethod: c ? c.method || null : null,
        vacancyThreshold: c ? c.vacancy_threshold ?? null : null,
        targetBelowThreshold: c ? c.target_below_threshold ?? null : null,
        targetAtOrAboveThreshold: c
          ? c.target_at_or_above_threshold ?? null
          : null,
        fixedTargetUnits: c ? c.fixed_target_units ?? null : null,
        devicesPerUnit: c ? c.devices_per_unit ?? null : null,
        frequency: {
          type: frequency?.type || "daily",
          interval: frequency?.interval || 1,
          daysOfWeek: frequency?.daysOfWeek || [],
          dayOfMonth: frequency?.dayOfMonth || null,
          monthOfYear: frequency?.monthOfYear || null,
        },
        priority: taskRule.priority || "moderate",
        startsAt: formatDateForPicker(taskRule.startsAt),
        endsAt: formatDateForPicker(taskRule.endsAt),
        status: taskRule.status || "active",
        actions: {
          photoVideoRequired: isManageOverlocksTask
            ? false
            : Boolean(actions.photoVideoRequired),
        },
        notificationPreference: cleanNotificationPreference,
      };

      if (this.defaultOrganization?.id && !this.taskRuleForm.organizationId) {
        this.taskRuleForm.organizationId = this.defaultOrganization.id;
      }

      if (!isManageOverlocksTask && frequency && frequency.type === "custom") {
        if (frequency.daysOfWeek && frequency.daysOfWeek.length > 0) {
          this.customUnit = "weeks";
        } else if (frequency.dayOfMonth) {
          this.customUnit = "months";
        } else if (frequency.monthOfYear) {
          this.customUnit = "years";
        } else {
          this.customUnit = "days";
        }
      }

      if (
        taskRule.actions &&
        taskRule.actions.checklist &&
        Array.isArray(taskRule.actions.checklist) &&
        taskRule.actions.checklist.length > 0
      ) {
        this.checklistItems = taskRule.actions.checklist.map((item) => ({
          description: item.description || "",
          required: Boolean(item.required),
        }));
        this.showInlineChecklist = true;
      } else {
        this.checklistItems = [{ description: "", required: true }];
        this.showInlineChecklist = false;
      }
    },

    getDefaultNotificationPreference() {
      return {
        notifyAssignment: {
          email: true,
          sms: false,
          managerRole: "facility_manager",
          robocall: false,
          slack: false,
          teams: false,
          summaryReportingSettings: {
            dailyConcerningActivity: false,
            dailyFacilitySummary: false,
            weeklyFacilitySummary: false,
            weeklyRegionalSummary: false,
          },
        },
        notifyTodo: {
          email: true,
          sms: false,
          managerRole: "facility_manager",
          robocall: false,
          slack: false,
          teams: false,
          summaryReportingSettings: {
            dailyConcerningActivity: false,
            dailyFacilitySummary: false,
            weeklyFacilitySummary: false,
            weeklyRegionalSummary: false,
          },
        },
        notifyOverdue: [
          {
            email: true,
            sms: false,
            managerRole: "facility_manager",
            robocall: false,
            slack: false,
            teams: false,
            summaryReportingSettings: {
              dailyConcerningActivity: false,
              dailyFacilitySummary: false,
              weeklyFacilitySummary: false,
              weeklyRegionalSummary: false,
            },
          },
          {
            email: true,
            sms: false,
            managerRole: "regional_manager",
            robocall: false,
            slack: false,
            teams: false,
            summaryReportingSettings: {
              dailyConcerningActivity: false,
              dailyFacilitySummary: false,
              weeklyFacilitySummary: false,
              weeklyRegionalSummary: false,
            },
          },
          {
            email: true,
            sms: false,
            managerRole: "organization_manager",
            robocall: false,
            slack: false,
            teams: false,
            summaryReportingSettings: {
              dailyConcerningActivity: false,
              dailyFacilitySummary: false,
              weeklyFacilitySummary: false,
              weeklyRegionalSummary: false,
            },
          },
        ],
      };
    },

    resetForm() {
      this.taskRuleForm = {
        organizationId:
          this.defaultOrganizationId || this.defaultOrganization?.id || "",
        name: "",
        taskTypeId: "",
        description: "",
        preInstallMethod: null,
        vacancyThreshold: null,
        targetBelowThreshold: null,
        targetAtOrAboveThreshold: null,
        fixedTargetUnits: null,
        devicesPerUnit: null,
        frequency: {
          type: "daily",
          interval: 1,
          daysOfWeek: [],
          dayOfMonth: null,
          monthOfYear: null,
        },
        priority: "moderate",
        startsAt: this.getCurrentDate(),
        endsAt: "2099-12-31",
        status: "active",
        actions: {
          photoVideoRequired: false,
        },
        notificationPreference: this.getDefaultNotificationPreference(),
      };

      this.checklistItems = [{ description: "", required: true }];
      this.showInlineChecklist = false;
      this.customUnit = "days";
      this.selectedOrganization = null;
      this.clearAllAlerts();
      this.$nextTick(() => {
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
      });
    },
    cleanFrequencyData() {
      const frequency = { ...this.taskRuleForm.frequency };

      switch (frequency.type) {
        case "one-time":
          return {
            type: frequency.type,
          };
        case "daily":
          return {
            type: frequency.type,
            interval: frequency.interval || 1,
          };
        case "weekly":
          return {
            type: frequency.type,
            interval: frequency.interval || 1,
            daysOfWeek:
              frequency.daysOfWeek && frequency.daysOfWeek.length > 0
                ? frequency.daysOfWeek
                : ["monday"],
          };
        case "monthly":
          return {
            type: frequency.type,
            interval: frequency.interval || 1,
            dayOfMonth: frequency.dayOfMonth || 1,
          };
        case "yearly":
          return {
            type: frequency.type,
            interval: frequency.interval || 1,
            monthOfYear: frequency.monthOfYear || 1,
          };
        case "custom":
          const customFreq = {
            type: frequency.type,
            interval: frequency.interval || 1,
          };

          if (frequency.daysOfWeek && frequency.daysOfWeek.length > 0) {
            customFreq.daysOfWeek = frequency.daysOfWeek;
          }
          if (frequency.dayOfMonth) {
            customFreq.dayOfMonth = frequency.dayOfMonth;
          }
          if (frequency.monthOfYear) {
            customFreq.monthOfYear = frequency.monthOfYear;
          }

          return customFreq;
        default:
          return frequency;
      }
    },

    async submitTaskRule() {
      this.clearAllAlerts();

      if (!this.isFormValid) {
        this.addLocalAlert({
          type: "error",
          message: "Please fill in all required fields",
          timeout: 5,
        });

        if (!this.hasValidNotificationPreference) {
          this.addLocalAlert({
            type: "error",
            message: this.notificationValidationMessage,
            timeout: 5,
          });
        }
        return;
      }

      try {
        this.loading = true;

        const cleanNotificationPreference = this.cleanGraphQLObject(
          this.taskRuleForm.notificationPreference
        );

        const actions = {
          photoVideoRequired: this.isManageOverlocks
            ? false
            : this.taskRuleForm.actions.photoVideoRequired,
        };

        if (this.isWalkaroundChecklist && this.checklistItems.length > 0) {
          const validChecklistItems = this.checklistItems.filter(
            (item) => item.description && item.description.trim()
          );

          if (validChecklistItems.length > 0) {
            actions.checklist = validChecklistItems.map((item) => ({
              description: item.description.trim(),
              required: Boolean(item.required),
            }));
          }
        }

        const taskRuleData = {
          name: this.taskRuleForm.name,
          description: this.taskRuleForm.description,
          status: this.taskRuleForm.status,
          priority: this.taskRuleForm.priority,
          taskTypeId: this.taskRuleForm.taskTypeId,
          frequency: this.cleanFrequencyData(),
          organizationId: this.taskRuleForm.organizationId,
          startsAt: new Date(this.taskRuleForm.startsAt).toISOString(),
          endsAt: new Date(this.taskRuleForm.endsAt).toISOString(),
          actions: actions,
          notificationPreference: cleanNotificationPreference,
        };

        if (this.isPreInstallDevices) {
          const conditions = {
            event: "pre_install_devices",
            method: this.taskRuleForm.preInstallMethod,
          };

          if (this.taskRuleForm.vacancyThreshold != null) {
            conditions.vacancy_threshold = Number(
              this.taskRuleForm.vacancyThreshold
            );
          }
          if (this.taskRuleForm.targetBelowThreshold != null) {
            conditions.target_below_threshold = Number(
              this.taskRuleForm.targetBelowThreshold
            );
          }
          if (this.taskRuleForm.targetAtOrAboveThreshold != null) {
            conditions.target_at_or_above_threshold = Number(
              this.taskRuleForm.targetAtOrAboveThreshold
            );
          }
          if (this.taskRuleForm.fixedTargetUnits != null) {
            conditions.fixed_target_units = Number(
              this.taskRuleForm.fixedTargetUnits
            );
          }
          if (this.taskRuleForm.devicesPerUnit != null) {
            conditions.devices_per_unit = Number(
              this.taskRuleForm.devicesPerUnit
            );
          }

          taskRuleData.conditions = conditions;
        }

        let mutation;
        let variables;

        if (this.isEditing && this.taskRule) {
          mutation = UPDATE_TASK_RULE;
          variables = {
            id: this.taskRule.id,
            ...taskRuleData,
          };
        } else {
          mutation = CREATE_TASK_RULE;
          variables = taskRuleData;
        }

        await this.$apollo.mutate({
          mutation,
          variables,
        });

        this.$emit("task-saved", {
          isEditing: this.isEditing,
          taskRuleData,
        });

        this.$emit("show-alert", {
          type: "success",
          message: `Task rule ${
            this.isEditing ? "updated" : "created"
          } successfully`,
          timeout: 5,
        });

        this.handleClose();
      } catch (error) {
        console.error("Error saving task rule:", error);
        this.addLocalAlert({
          type: "error",
          message: `Error ${
            this.isEditing ? "updating" : "creating"
          } task rule: ${error.message}`,
          timeout: 10,
        });
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$emit("input", false);
      this.$nextTick(() => {
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.resetForm();
      });
    },

    closeNotificationModal() {
      this.showNotificationModal = false;
    },

    handleNotificationSave(notificationSettings) {
      const cleanSettings = this.cleanGraphQLObject(notificationSettings);
      this.taskRuleForm.notificationPreference = cleanSettings;
      this.showNotificationModal = false;
      this.addLocalAlert({
        type: "success",
        message: "Notification settings saved successfully",
        timeout: 5,
      });
    },

    handleNotificationValidationError(message) {
      this.addLocalAlert({
        type: "error",
        message: message,
        timeout: 5,
      });
    },
    titleCase,
  },
};
</script>

<style scoped>
.v-card {
  box-shadow: none !important;
}

.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.method-container {
  border: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 16px;
  padding: 15px 10px;
}
.inline-number-input {
  display: inline-block;
  width: 80px;
  margin: 0 8px;
}
</style>
